import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useOutletContext } from 'react-router-dom';
import api from '../../api';
import { PORTAL_LINK_UNAVAILABLE, portalTokenErrorMessage } from './portalTokenMessages';

/**
 * ZRF-WTPB-011 — private document list via portal token (not group-safe).
 */
const PortalDocumentsPage = () => {
  const { token, tokenHint, validation, validationLoading, validationError } =
    useOutletContext() || {};

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');

  const scopeError =
    validation && validation.valid
      ? portalTokenErrorMessage(validation, { requiredActionType: 'view_documents' })
      : null;

  const linkUnavailable =
    Boolean(validationError) ||
    (validation && validation.valid === false) ||
    Boolean(scopeError);

  const unavailableMessage =
    validationError ||
    (validation && validation.valid === false
      ? portalTokenErrorMessage(validation)
      : null) ||
    scopeError ||
    PORTAL_LINK_UNAVAILABLE;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!token || linkUnavailable) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError('');
      try {
        const res = await api.get(
          `/api/portal/tokens/${encodeURIComponent(token)}/documents`
        );
        if (cancelled) return;
        if (res.data?.valid === false) {
          setError(portalTokenErrorMessage(res.data) || PORTAL_LINK_UNAVAILABLE);
          setDocuments([]);
          return;
        }
        setDocuments(Array.isArray(res.data?.documents) ? res.data.documents : []);
        console.log('🔍 portal documents listed', {
          count: res.data?.documents?.length || 0,
          tokenHint,
        });
      } catch (err) {
        console.error('❌ portal documents load failed:', err);
        if (!cancelled) {
          const data = err?.response?.data;
          setError(portalTokenErrorMessage(data) || PORTAL_LINK_UNAVAILABLE);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [token, linkUnavailable, tokenHint]);

  const openDocument = async (doc, mode) => {
    setActionError('');
    if (!token || !doc?.documentId) return;
    try {
      // Log access; prefer JSON (redirect=0) then open fileUrl privately
      const res = await api.get(
        `/api/portal/tokens/${encodeURIComponent(token)}/documents/${encodeURIComponent(doc.documentId)}/download`,
        { params: { redirect: '0', mode: mode === 'view' ? 'view' : 'download' } }
      );
      if (res.data?.valid === false) {
        setActionError(portalTokenErrorMessage(res.data) || PORTAL_LINK_UNAVAILABLE);
        return;
      }
      const url = res.data?.fileUrl || doc.downloadUrl;
      if (url && /^https?:\/\//i.test(url)) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        setActionError('This document is not available for download right now.');
      }
      console.log('✅ portal document access', {
        documentId: doc.documentId,
        mode,
        timelineEventId: res.data?.timelineEventId,
      });
    } catch (err) {
      console.error('❌ portal document open failed:', err);
      const data = err?.response?.data;
      setActionError(
        portalTokenErrorMessage(data) ||
          data?.error?.message ||
          'Could not open the document. Please try again.'
      );
    }
  };

  if (validationLoading || loading) {
    return (
      <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
        <CircularProgress size={36} />
        <Typography variant="body2" color="text.secondary">
          Loading your documents…
        </Typography>
      </Stack>
    );
  }

  if (linkUnavailable || error) {
    return (
      <Stack spacing={2}>
        <Typography variant="h5" component="h1">
          Link unavailable
        </Typography>
        <Alert severity="warning">{error || unavailableMessage}</Alert>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant="h5" component="h1">
        Your documents
      </Typography>
      <Typography variant="body2" color="text.secondary">
        These files are private to you. Do not share this link in a group chat.
      </Typography>
      {actionError ? <Alert severity="error">{actionError}</Alert> : null}
      {documents.length === 0 ? (
        <Alert severity="info">No documents are available for this link yet.</Alert>
      ) : (
        <List disablePadding>
          {documents.map((doc, index) => (
            <React.Fragment key={doc.documentId}>
              {index > 0 ? <Divider component="li" /> : null}
              <ListItem
                alignItems="flex-start"
                sx={{ px: 0, flexDirection: 'column', alignItems: 'stretch', gap: 1 }}
              >
                <ListItemText
                  primary={doc.title || 'Document'}
                  secondary={doc.type || 'document'}
                />
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => openDocument(doc, 'view')}
                  >
                    View
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => openDocument(doc, 'download')}
                  >
                    Download
                  </Button>
                </Stack>
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      )}
      <Button
        component={RouterLink}
        to={`/p/t/${encodeURIComponent(token || '')}`}
        variant="text"
      >
        Back
      </Button>
    </Stack>
  );
};

export default PortalDocumentsPage;
